import { useTranslation } from 'react-i18next'
import moment from 'moment'
import TableItems from '../Table/TableItems'
import { Cancellation } from '../../interfaces/cancellation'
import { CsvColumn } from '../../utils/toCsv'
import { formatCancellationReason } from '../../helpers/formatCancellationReason'
import { sortDateNullsLast, sortTextNullsLast } from '../../utils/tableSort'

// Newest first, matching the endpoint's own `-created_at` default so the
// first paint does not reshuffle the rows the server already ordered.
const DEFAULT_SORT = [{ id: 'created_at', desc: true }]

interface Props {
  isLoading: boolean
  cancellations: Cancellation[]
}

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '-'

const formatDateTime = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY, HH:mm') : '-'

// `plan_interval` mirrors `PricingPlan.interval` ('monthly' | 'yearly'), but
// that branch of the API is unverified here, so an unrecognised value is shown
// as it arrived rather than swallowed.
const formatInterval = (
  t: (key: string) => string,
  interval?: string | null,
) => {
  const key = (interval ?? '').trim()
  if (!key) return ''
  if (key === 'monthly' || key === 'yearly') return t(key)
  return key
}

const formatPlan = (t: (key: string) => string, row: Cancellation) => {
  const interval = formatInterval(t, row.plan_interval)
  if (!row.plan_name) return interval || '-'
  return interval ? `${row.plan_name} (${interval})` : row.plan_name
}

/** Who cancelled: the name when the backend has one, the email otherwise -
 *  the email is what the reason email to the super admins quotes. */
const formatCancelledBy = (row: Cancellation) =>
  row.cancelled_by_name?.trim() || row.cancelled_by_email?.trim() || '-'

// Shared by the CSV export and the table so both describe the same rows. The
// comment goes out in full even though the column truncates it - the whole
// point of exporting is to read what people wrote.
export const cancellationCsvColumns = (
  t: (key: string) => string,
): CsvColumn<Cancellation>[] => [
  { header: t('organization'), value: (row) => row.organization_name ?? '' },
  { header: t('plan'), value: (row) => formatPlan(t, row) },
  { header: t('cancelledBy'), value: (row) => formatCancelledBy(row) },
  { header: t('email'), value: (row) => row.cancelled_by_email ?? '' },
  {
    header: t('reason'),
    value: (row) => formatCancellationReason(t, row.reason, row.reason_label),
  },
  { header: t('comment'), value: (row) => row.comment ?? '' },
  { header: t('cancelledOn'), value: (row) => formatDateTime(row.created_at) },
  { header: t('accessEnds'), value: (row) => formatDate(row.period_end) },
  {
    header: t('status'),
    value: (row) => t(row.reverted_at ? 'reversed' : 'stillStanding'),
  },
]

const CancellationsList = ({ isLoading, cancellations = [] }: Props) => {
  const { t } = useTranslation()

  const columns = [
    {
      Header: t('organization'),
      accessor: 'organization_name',
      sortType: sortTextNullsLast,
      Cell: ({ value }: { value: Cancellation['organization_name'] }) => (
        <span className='text-black/80'>{value || '-'}</span>
      ),
    },
    {
      Header: t('plan'),
      id: 'plan',
      accessor: (row: Cancellation) => formatPlan(t, row),
      sortType: sortTextNullsLast,
    },
    {
      Header: t('cancelledBy'),
      id: 'cancelled_by',
      accessor: (row: Cancellation) => formatCancelledBy(row),
      sortType: sortTextNullsLast,
      Cell: ({ row: { original } }: { row: { original: Cancellation } }) => (
        <span title={original.cancelled_by_email ?? ''}>
          {formatCancelledBy(original)}
        </span>
      ),
    },
    {
      // Sorted on what the column draws, not the raw key, so the order on
      // screen matches the words on screen in every language.
      Header: t('reason'),
      id: 'reason',
      accessor: (row: Cancellation) =>
        formatCancellationReason(t, row.reason, row.reason_label),
      sortType: sortTextNullsLast,
    },
    {
      Header: t('comment'),
      accessor: 'comment',
      sortType: sortTextNullsLast,
      // Capped at 2000 characters by the backend, so it needs a ceiling here
      // rather than a column that grows to fit the longest answer.
      Cell: ({ value }: { value: Cancellation['comment'] }) =>
        value ? (
          <span className='block max-w-[220px] truncate' title={value}>
            {value}
          </span>
        ) : (
          '-'
        ),
    },
    {
      Header: t('cancelledOn'),
      accessor: 'created_at',
      sortType: sortDateNullsLast,
      Cell: ({ value }: { value: Cancellation['created_at'] }) => (
        <span>{formatDateTime(value)}</span>
      ),
    },
    {
      Header: t('accessEnds'),
      accessor: 'period_end',
      sortType: sortDateNullsLast,
      Cell: ({ value }: { value: Cancellation['period_end'] }) => (
        <span>{formatDate(value)}</span>
      ),
    },
    {
      // A reverted cancellation is the good outcome - the customer came back -
      // so it reads green against the red of one that still stands.
      Header: t('status'),
      accessor: 'reverted_at',
      sortType: sortDateNullsLast,
      Cell: ({ value }: { value: Cancellation['reverted_at'] }) =>
        value ? (
          <span className='text-[#21BA45]' title={formatDateTime(value)}>
            {t('reversed')}
          </span>
        ) : (
          <span className='text-deleteRed'>{t('stillStanding')}</span>
        ),
    },
  ]

  return (
    <div className='w-full min-h-[710px] px-5 py-5 md:px-10 md:py-6 lg:px-20 lg:py-11 bg-white rounded-20px shadow-form mt-7'>
      <TableItems
        columns={columns}
        data={cancellations}
        isDataLoading={isLoading}
        noDataFoundMessage='noCancellationsFound'
        showSearch={false}
        isSortable
        defaultSortBy={DEFAULT_SORT}
        showLastColumn={false}
        pageSize={6}
        tableHeightClassName='h-[660px]'
      />
    </div>
  )
}

export default CancellationsList
